import { useMemo } from 'react';
import { DateTime } from 'luxon';

import { emptyArrayOf } from 'ui-components/default-props/empty';

import { toDisplayableTimestamp } from '../../../../../inventory-sync/is-min-date';
import { parseLastJobInfo } from '../../../../../inventory-sync/parse-last-job-info';
import { useInventorySyncConnections } from '../../../../../inventory-sync/hooks/use-inventory-sync-connections';
import { CONNECTION_STATUS, JOB_STATUS, TERMINUS_TYPE } from '../../../../../inventory-sync/types/inventory-sync-types';
import { SYNC_INELIGIBLE_REASON } from './product-inventory-sync-types';

import type { ProductConnectionSyncRow, ProductSyncSummary, SyncIneligibleReason } from './product-inventory-sync-types';
import type { InventorySyncConnection, JobResult } from '../../../../../inventory-sync/types/inventory-sync-types';

const FAILED_JOB_STATUSES: string[] = [JOB_STATUS.failed, JOB_STATUS.killed];
const IN_PROGRESS_JOB_STATUSES: string[] = [JOB_STATUS.new, JOB_STATUS.enqueued, JOB_STATUS.running];

const isSameSku = (candidate: string | undefined, productSku: string): boolean =>
  typeof candidate === 'string' && candidate.toLowerCase() === productSku.toLowerCase();

const toSkuList = <TEntry>(list: TEntry[] | undefined): TEntry[] => (Array.isArray(list) ? list : []);

const findPushedQuantityForSku = (result: JobResult | undefined, productSku: string): number | null =>
  toSkuList(result?.pushed_skus).find(({ sku }) => isSameSku(sku, productSku))?.totalAvailable ?? null;

const findSkuError = (result: JobResult | undefined, productSku: string): { sku: string; errorMessage?: string } | undefined =>
  toSkuList(result?.skus_with_errors).find(({ sku }) => isSameSku(sku, productSku));

const resolveIneligibleReason = (connection: InventorySyncConnection): SyncIneligibleReason | null => {
  if (connection.terminusType !== TERMINUS_TYPE.target) {
    return SYNC_INELIGIBLE_REASON.notATarget;
  }

  return null;
};

const toRowFromConnection = (connection: InventorySyncConnection, productSku: string): ProductConnectionSyncRow => {
  const lastJobInfo = parseLastJobInfo(connection.lastJobInfo);
  const ineligibleReason = resolveIneligibleReason(connection);
  const skuError = findSkuError(lastJobInfo?.result, productSku);

  const isSyncing = IN_PROGRESS_JOB_STATUSES.includes(lastJobInfo?.status ?? '');
  const hasSyncError = !isSyncing && (FAILED_JOB_STATUSES.includes(lastJobInfo?.status ?? '') || skuError !== undefined);

  return {
    connectionId: connection.connectionId,
    connectionName: connection.name,
    apiCode: connection.apiCode,
    terminusType: connection.terminusType,
    connectionStatus: connection.status,
    ineligibleReason,
    lastSyncedAt: toDisplayableTimestamp(connection.lastRefreshedAt),
    lastSyncedQuantity: findPushedQuantityForSku(lastJobInfo?.result, productSku),
    nextSyncQuantity: null,
    config: connection.config ?? {},
    isSyncing,
    hasSyncError,
    syncErrorMessage: skuError?.errorMessage ?? lastJobInfo?.result?.error_message ?? null,
    connection,
  };
};

/** Milliseconds for a timestamp Luxon can parse, otherwise null. */
export const toSortableTime = (timestamp: string | null | undefined): number | null => {
  if (!timestamp) {
    return null;
  }

  const millis = DateTime.fromISO(timestamp).toMillis();
  return Number.isFinite(millis) ? millis : null;
};

const findLatestSourceRefreshedAt = (connections: readonly InventorySyncConnection[]): string | null =>
  connections
    .filter(({ terminusType }) => terminusType === TERMINUS_TYPE.source)
    .map(({ lastRefreshedAt }) => toDisplayableTimestamp(lastRefreshedAt))
    .filter((timestamp): timestamp is string => timestamp !== null && toSortableTime(timestamp) !== null)
    .sort((left, right) => (toSortableTime(left) ?? 0) - (toSortableTime(right) ?? 0))
    .at(-1) ?? null;

export const useProductConnectionSync = (productSku: string) => {
  const {
    connections,
    isLoading: areConnectionsLoading,
    error: connectionsError,
    refetch: refetchConnections,
  } = useInventorySyncConnections();

  const rows = useMemo(
    () =>
      productSku
        ? connections
            .filter(
              ({ status, supportedTerminusTypes }) =>
                status !== CONNECTION_STATUS.disabled && supportedTerminusTypes?.includes(TERMINUS_TYPE.target),
            )
            .map((connection) => toRowFromConnection(connection, productSku))
        : emptyArrayOf<ProductConnectionSyncRow>(),
    [connections, productSku],
  );

  const summary: ProductSyncSummary = useMemo(
    () => ({
      inStockQuantity: null,
      lastUpdatedAt: findLatestSourceRefreshedAt(connections),
    }),
    [connections],
  );

  return {
    rows,
    summary,
    isLoading: areConnectionsLoading,
    hasLoadError: Boolean(connectionsError),
    refetch: refetchConnections,
  };
};
